// "Auto Variants": theory-based palette generation for the ACTIVE palette.
//
// Generation is delegated to @imagelayerdecomposer/core (pure, deterministic,
// LCH-based). This component only owns the UI state (theory, anchor, options,
// shuffle seed, selection) and asks App to preview / add colorways.
//
// Layer locks are keyed by PaletteColor.id so they survive colorway switches
// and drag-reordering of layers.
//
// Note: swatch previews are color strips (no per-variant rendered thumbnail):
// rendering N full image previews at once would cost more than the interactive
// budget allows, and the canvas already shows the hovered variant live.

import React, { useEffect, useMemo, useState } from 'react';
import type { PaletteColor } from '@imagelayerdecomposer/core';
import {
  COLOR_THEORIES,
  generateVariants,
  labToHex,
  type ColorTheory,
  type PaperMode,
} from '@imagelayerdecomposer/core';

interface AutoVariantsPanelProps {
  colors: PaletteColor[];
  coverage: number[];
  lockedIds: string[];
  onToggleLock: (id: string) => void;
  /** Hover preview: null = restore the active colorway. */
  onPreview: (colors: PaletteColor[] | null) => void;
  onAddColorway: (colors: PaletteColor[], name: string) => void;
  onAddAll: (variants: PaletteColor[][], label: string) => void;
}

function hexOf(color: PaletteColor): string {
  try {
    return labToHex(color.lab);
  } catch {
    return '#888888';
  }
}

function layerName(color: PaletteColor, index: number): string {
  return color.isPaper || index === 0 ? 'Paper' : `Color ${index}`;
}

export default function AutoVariantsPanel({
  colors,
  coverage,
  lockedIds,
  onToggleLock,
  onPreview,
  onAddColorway,
  onAddAll,
}: AutoVariantsPanelProps) {
  const [theory, setTheory] = useState<ColorTheory>('complementary');
  const [paperMode, setPaperMode] = useState<PaperMode>('keep');
  const [preserveLightness, setPreserveLightness] = useState(true);
  const [count, setCount] = useState(6);
  const [seed, setSeed] = useState(0);
  const [selected, setSelected] = useState(0);
  const [anchorId, setAnchorId] = useState('');

  // Default anchor: the layer with the largest coverage, paper excluded.
  const defaultAnchorId = useMemo(() => {
    let bestId = '';
    let bestCoverage = -1;
    colors.forEach((color, i) => {
      if (color.isPaper || i === 0) return;
      const cov = coverage[i] ?? 0;
      if (cov > bestCoverage) {
        bestCoverage = cov;
        bestId = color.id;
      }
    });
    return bestId || colors[0]?.id || '';
  }, [colors, coverage]);

  useEffect(() => {
    if (!colors.some((c) => c.id === anchorId)) setAnchorId(defaultAnchorId);
  }, [colors, anchorId, defaultAnchorId]);

  const anchorIndex = Math.max(
    0,
    colors.findIndex((c) => c.id === anchorId)
  );

  const variants = useMemo(() => {
    if (colors.length === 0) return [];
    return generateVariants({
      theory,
      colors,
      anchorIndex,
      paperMode,
      locked: colors.map((c) => lockedIds.includes(c.id)),
      preserveLightness,
      count,
      seed,
    });
  }, [colors, theory, anchorIndex, paperMode, lockedIds, preserveLightness, count, seed]);

  // Lab result -> full PaletteColor (ids/isPaper stay tied to the layer).
  const variantPalettes = useMemo(
    () =>
      variants.map((labs) =>
        colors.map((color, i) => ({ ...color, lab: labs[i] }))
      ),
    [variants, colors]
  );

  useEffect(() => {
    if (selected >= variantPalettes.length) setSelected(0);
  }, [selected, variantPalettes.length]);

  const selectedPalette = variantPalettes[selected] ?? null;

  const handleSelect = (index: number) => {
    setSelected(index);
    onPreview(variantPalettes[index] ?? null);
  };

  const handleShuffle = () => {
    setSeed((s) => s + 1);
    setSelected(0);
    onPreview(null);
  };

  const handleAddSelected = () => {
    if (!selectedPalette) return;
    const label = COLOR_THEORIES.find((t) => t.id === theory)?.label ?? theory;
    onAddColorway(selectedPalette, `${label} ${selected + 1}`);
    onPreview(null);
  };

  const handleAddAll = () => {
    if (variantPalettes.length === 0) return;
    const label = COLOR_THEORIES.find((t) => t.id === theory)?.label ?? theory;
    onAddAll(variantPalettes, label);
    onPreview(null);
  };

  if (colors.length === 0) {
    return (
      <div className="panel">
        <div className="panel-header">
          <span>Auto Variants</span>
        </div>
        <div className="hint">Run a separation first to generate variants.</div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <span>Auto Variants</span>
      </div>

      <div className="field-block">
        <label className="field-label">Color theory</label>
        <div className="segmented theory-grid" role="group" aria-label="Color theory">
          {COLOR_THEORIES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`seg-btn${theory === t.id ? ' active' : ''}`}
              aria-pressed={theory === t.id}
              onClick={() => {
                setTheory(t.id);
                setSeed(0);
                setSelected(0);
                onPreview(null);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field-block">
        <label className="field-label" htmlFor="anchor-layer">
          Anchor layer
        </label>
        <select
          id="anchor-layer"
          value={anchorId}
          onChange={(e) => {
            setAnchorId(e.target.value);
            setSeed(0);
          }}
        >
          {colors.map((color, i) => (
            <option key={color.id} value={color.id}>
              {layerName(color, i)}
              {coverage[i] !== undefined ? ` — ${coverage[i].toFixed(1)}%` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="field-block">
        <label className="field-label">Paper color</label>
        <div className="segmented" role="group" aria-label="Paper handling">
          <button
            type="button"
            className={`seg-btn${paperMode === 'keep' ? ' active' : ''}`}
            aria-pressed={paperMode === 'keep'}
            onClick={() => setPaperMode('keep')}
          >
            Keep
          </button>
          <button
            type="button"
            className={`seg-btn${paperMode === 'include' ? ' active' : ''}`}
            aria-pressed={paperMode === 'include'}
            onClick={() => setPaperMode('include')}
          >
            Include in variants
          </button>
        </div>
      </div>

      <label className="check-row">
        <input
          type="checkbox"
          checked={preserveLightness}
          onChange={(e) => setPreserveLightness(e.target.checked)}
        />
        Preserve lightness
      </label>

      <div className="field-block">
        <label className="field-label" htmlFor="variant-count">
          Variant count: <span className="value-badge">{count}</span>
        </label>
        <input
          id="variant-count"
          type="range"
          min={4}
          max={8}
          step={1}
          value={count}
          onChange={(e) => {
            setCount(parseInt(e.target.value, 10));
            setSelected(0);
            onPreview(null);
          }}
        />
      </div>

      <div className="field-block">
        <label className="field-label">Layer lock</label>
        <div className="lock-row">
          {colors.map((color, i) => {
            const locked = lockedIds.includes(color.id);
            return (
              <button
                key={color.id}
                type="button"
                className={`lock-chip${locked ? ' locked' : ''}`}
                title={`${layerName(color, i)} — ${locked ? 'locked (unchanged in variants)' : 'unlocked'}`}
                aria-pressed={locked}
                onClick={() => onToggleLock(color.id)}
              >
                <span className="lock-swatch" style={{ backgroundColor: hexOf(color) }} />
                <span className="lock-icon">{locked ? '🔒' : '🔓'}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="btn-row variant-actions">
        <button type="button" className="btn small" onClick={handleShuffle}>
          Shuffle
        </button>
        <button
          type="button"
          className="btn small secondary"
          onClick={handleAddAll}
          disabled={variantPalettes.length === 0}
        >
          Add all
        </button>
      </div>

      <div className="variant-grid" role="listbox" aria-label="Generated variants">
        {variantPalettes.map((palette, index) => (
          <button
            key={index}
            type="button"
            role="option"
            aria-selected={selected === index}
            className={`variant-card${selected === index ? ' selected' : ''}`}
            title={`Variant ${index + 1} — click to preview`}
            onClick={() => handleSelect(index)}
            onMouseEnter={() => onPreview(palette)}
            onMouseLeave={() => onPreview(selectedPalette)}
            onFocus={() => onPreview(palette)}
            onBlur={() => onPreview(selectedPalette)}
          >
            <span className="variant-strip">
              {palette.map((color) => (
                <span
                  key={color.id}
                  className="variant-swatch"
                  style={{ backgroundColor: hexOf(color) }}
                />
              ))}
            </span>
            <span className="variant-index">{index + 1}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="btn primary add-variant"
        onClick={handleAddSelected}
        disabled={!selectedPalette}
      >
        Add as Colorway
      </button>

      <p className="hint">
        Hover previews on the canvas. Locked layers keep their exact color.
      </p>
    </div>
  );
}
